import type { ChatCitation } from '@/features/shared/chat/types'

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function readString(value: unknown) {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined
}

function readNumber(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value)

    return Number.isFinite(parsed) ? parsed : undefined
  }

  return undefined
}

function readPage(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  return readString(value)
}

function normalizeCitationResource(value: unknown): ChatCitation | undefined {
  if (!isRecord(value)) {
    return undefined
  }

  const content = readString(value.content) ?? readString(value.summary) ?? ''
  const documentName =
    readString(value.document_name) ?? readString(value.documentName) ?? readString(value.title)
  const datasetName = readString(value.dataset_name) ?? readString(value.datasetName)

  if (!content && !documentName && !datasetName) {
    return undefined
  }

  return {
    content,
    dataSourceType: readString(value.data_source_type) ?? readString(value.dataSourceType),
    datasetId: readString(value.dataset_id) ?? readString(value.datasetId),
    datasetName,
    documentId: readString(value.document_id) ?? readString(value.documentId),
    documentName,
    hitCount: readNumber(value.hit_count ?? value.hitCount),
    indexNodeHash: readString(value.index_node_hash) ?? readString(value.indexNodeHash),
    page: readPage(value.page),
    position: readNumber(value.position),
    score: readNumber(value.score),
    segmentId: readString(value.segment_id) ?? readString(value.segmentId),
    segmentPosition: readNumber(value.segment_position ?? value.segmentPosition),
    wordCount: readNumber(value.word_count ?? value.wordCount),
  }
}

export function normalizeCitationResources(value: unknown): ChatCitation[] {
  if (!Array.isArray(value)) {
    return []
  }

  return value.flatMap((item) => {
    const citation = normalizeCitationResource(item)

    return citation ? [citation] : []
  })
}

export function normalizeCitationResourcesFromMetadata(metadata: unknown) {
  if (!isRecord(metadata)) {
    return []
  }

  return normalizeCitationResources(metadata.retriever_resources)
}
